import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import { Loader2, Send, GraduationCap, Building2, Users, Sparkles } from "lucide-react";
import { API, COMPASS_LOGO_URL, COMPASS_LOGO_DARK_URL } from "@/lib/brand";
import { useTheme } from "@/lib/theme";

const TYPE_OPTIONS = [
  { value: "college", label: "College / University", icon: GraduationCap },
  { value: "corporate", label: "Corporate / Organization", icon: Building2 },
  { value: "community", label: "Community Event", icon: Users },
  { value: "other", label: "Other", icon: Sparkles },
];

const empty = {
  name: "", email: "", phone: "", institution: "",
  type: "", preferred_dates: "", audience_size: "", message: "",
};

export default function Compass() {
  const { theme } = useTheme();
  const [form, setForm] = useState(empty);
  const [state, setState] = useState("idle"); // idle | submitting | done
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const logoSrc = theme === "dark" ? COMPASS_LOGO_DARK_URL : COMPASS_LOGO_URL;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim() || !form.institution.trim() || !form.type) {
      toast.error("Please fill all required fields");
      return;
    }
    setState("submitting");
    try {
      const res = await fetch(`${API}/compass/request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(typeof err?.detail === "string" ? err.detail : "Could not send request");
      }
      setState("done");
      toast.success("Request received");
    } catch (err) {
      toast.error(err.message || "Something went wrong");
      setState("idle");
    }
  };

  return (
    <div>
      <Helmet>
        <title>Reliserve Wealth Compass — Financial Literacy Initiative</title>
        <meta name="description" content="Reliserve Wealth Compass is our financial education initiative — seminars, talks and webinars on investing and financial planning for students, professionals and organizations." />
        <meta property="og:title" content="Reliserve Wealth Compass" />
        <meta property="og:description" content="Financial literacy shouldn't be a privilege — it should be a starting point." />
        <meta property="og:image" content={COMPASS_LOGO_URL} />
      </Helmet>

      {/* Hero */}
      <section className="border-b hairline">
        <div className="max-w-5xl mx-auto px-6 md:px-8 pt-14 md:pt-20 pb-12 md:pb-16">
          <img
            src={logoSrc}
            alt="Reliserve Wealth Compass"
            className="h-16 md:h-20 w-auto"
            data-testid="compass-logo"
          />
          <p className="mt-8 font-display text-2xl md:text-3xl lg:text-4xl text-ink leading-[1.15] max-w-3xl">
            Financial literacy shouldn't be a privilege —{" "}
            <em className="italic text-brass-deep">it should be a starting point.</em>
          </p>
        </div>
      </section>

      {/* Body copy */}
      <section className="border-b hairline bg-paper-raised">
        <div className="max-w-5xl mx-auto px-6 md:px-8 py-16 md:py-20">
          <div className="grid md:grid-cols-12 gap-10">
            <div className="md:col-span-5">
              <div className="eyebrow">The initiative</div>
              <p className="font-display text-[22px] md:text-[24px] leading-snug text-ink mt-4">
                Informed decisions lead to better outcomes — and everyone deserves access
                to that understanding, not just those who can already afford professional advice.
              </p>
            </div>
            <div className="md:col-span-7 space-y-5 text-[16px] text-ink-soft leading-relaxed">
              <p>
                Reliserve Wealth Compass is our dedicated financial education initiative,
                built on a simple belief: informed decisions lead to better outcomes.
              </p>
              <p>
                Through Compass, we conduct regular <strong className="text-ink">seminars, talks
                and webinars</strong> designed to demystify investing and financial planning for
                students, working professionals, and anyone looking to build a stronger
                relationship with their money.
              </p>
              <p>
                Whether it's a first introduction to how mutual funds work, a deeper look at
                goal-based planning, or a conversation about why insurance and investing should
                never be confused with one another — <em>our sessions are built to educate, not to sell.</em>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Request Form */}
      <section id="request" className="border-b hairline">
        <div className="max-w-5xl mx-auto px-6 md:px-8 py-16 md:py-20">
          <div className="max-w-3xl">
            <div className="eyebrow mb-3">Bring Compass to your institution</div>
            <h2 className="font-display text-3xl md:text-4xl text-ink leading-[1.15]">
              Bring Compass to your college, university, organization, or event.
            </h2>
            <p className="text-ink-soft text-[15.5px] leading-relaxed mt-5">
              If you'd like us to conduct a Reliserve Wealth Compass session for your
              institution, fill out the form below and we'll reach out to discuss the details.
            </p>
          </div>

          {state === "done" ? (
            <div className="mt-10 card-paper p-8 md:p-10 max-w-3xl" data-testid="compass-done">
              <div className="eyebrow mb-2">Request received</div>
              <p className="font-display text-2xl md:text-3xl text-ink leading-tight">
                Thanks — we've received your request.
              </p>
              <p className="text-ink-soft text-[15px] leading-relaxed mt-4">
                We'll reach out shortly to discuss the details. In the meantime, a confirmation
                is on its way to <span className="num text-ink">{form.email}</span>.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-10 max-w-3xl space-y-5" data-testid="compass-form">
              <div className="grid md:grid-cols-2 gap-5">
                <Field label="Your name*" required>
                  <input className="fld" value={form.name} onChange={(e) => set({ name: e.target.value })} data-testid="compass-name" required />
                </Field>
                <Field label="Email*" required>
                  <input type="email" className="fld" value={form.email} onChange={(e) => set({ email: e.target.value })} data-testid="compass-email" required />
                </Field>
                <Field label="Phone*" required>
                  <input type="tel" className="fld num" value={form.phone} onChange={(e) => set({ phone: e.target.value })} data-testid="compass-phone" required />
                </Field>
                <Field label="Institution / Organization*" required>
                  <input className="fld" value={form.institution} onChange={(e) => set({ institution: e.target.value })} data-testid="compass-institution" required />
                </Field>
              </div>

              <Field label="Type*" required>
                <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-2" data-testid="compass-type">
                  {TYPE_OPTIONS.map((t) => {
                    const active = form.type === t.value;
                    const Icon = t.icon;
                    return (
                      <button
                        key={t.value}
                        type="button"
                        onClick={() => set({ type: t.value })}
                        className={`flex items-center gap-2 px-3 py-2.5 rounded-md border text-left text-[13px] transition-colors ${
                          active ? "border-ink bg-paper-raised text-ink" : "border-line text-ink-soft hover:border-ink hover:text-ink"
                        }`}
                        data-testid={`compass-type-${t.value}`}
                      >
                        <Icon size={14} /> {t.label}
                      </button>
                    );
                  })}
                </div>
              </Field>

              <div className="grid md:grid-cols-2 gap-5">
                <Field label="Preferred date(s)">
                  <input className="fld" placeholder="e.g. Any Saturday in October" value={form.preferred_dates} onChange={(e) => set({ preferred_dates: e.target.value })} data-testid="compass-dates" />
                </Field>
                <Field label="Expected audience size">
                  <input className="fld num" placeholder="e.g. 50–100" value={form.audience_size} onChange={(e) => set({ audience_size: e.target.value })} data-testid="compass-size" />
                </Field>
              </div>

              <Field label="Topic of interest / message">
                <textarea
                  className="fld min-h-[110px] resize-y"
                  placeholder="Tell us a bit about your group and what you'd like the session to cover."
                  value={form.message}
                  onChange={(e) => set({ message: e.target.value })}
                  data-testid="compass-message"
                />
              </Field>

              <button type="submit" className="btn-brass" disabled={state === "submitting"} data-testid="compass-submit">
                {state === "submitting" ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                Request a Compass Session
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="eyebrow block mb-2">{label}</span>
      {children}
    </label>
  );
}
